import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, LoginSchemaType } from "../../domain/validation/auth.schema";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Link } from "react-router-dom";
import { EyeOpenIcon, EyeClosedIcon } from "@radix-ui/react-icons";

interface LoginFormProps {
  onSubmit: (data: LoginSchemaType) => Promise<void>;
  isLoading?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, isLoading = false }) => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginSchemaType>({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
  });

  return (
    <Card className="w-full max-w-md bg-white shadow-none border border-border-gray rounded-none">
      <CardHeader className="space-y-2 pb-6 pt-8">
        <CardTitle className="text-center text-xl font-bold uppercase tracking-wide">
          ENTER YOUR LOGIN DETAILS
        </CardTitle>
      </CardHeader>
      <CardContent className="px-8 pb-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
          <div className="relative pb-5">
            <Label htmlFor="email" className="sr-only">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="E-mail"
              {...register("email")}
              error={!!errors.email}
              className="w-full h-12 bg-disabled-bg border-border-gray rounded-none placeholder:text-gray-500"
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="relative pb-5">
            <Label htmlFor="password" className="sr-only">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                {...register("password")}
                error={!!errors.password}
                className="w-full h-12 bg-disabled-bg border-border-gray rounded-none placeholder:text-gray-500 pr-10"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={isLoading}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeClosedIcon className="h-5 w-5" />
                ) : (
                  <EyeOpenIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.password.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-primary-blue hover:bg-primary-blue/90 text-white font-normal rounded-none mt-6"
            disabled={isLoading || !isValid}
          >
            {isLoading ? "Entering..." : "Enter"}
          </Button>
        </form>

        <div className="flex justify-center items-center space-x-2 text-sm mt-6">
          <Link to="/forgot-password" className="text-link-blue hover:underline">
            Forgot your password?
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/signup" className="text-link-blue hover:underline">
            Sign Up
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};
